/**
 * Three doors from a key to a word, and nothing else in the app has one.
 *
 * A spec, a row, and a screen carry keys. This is the only place a key becomes
 * a word. A missing key is a defect in the catalogue, not a chance to invent a
 * fallback sentence — the throw lives in `i18next.ts`'s
 * `parseMissingKeyHandler` and every door but `tOr` goes through it.
 *
 * **It lives here, below both trees, because the design system has to be able
 * to say a word.** *Writing in the UI* rule 2 is "never put a word in a
 * screen — every string is a key in `en.ts`", and for as long as this function
 * lived in `apps/mobile` that rule was unkeepable inside `packages/ui`: the
 * package could not import it, so eighteen English words were typed into
 * components and there was no legal alternative. A rule a whole package cannot
 * obey is not a rule, so the seam moved rather than the rule bending.
 *
 * `apps/mobile/src/lib/i18n.ts` re-exports all three, so the ~180 screen files
 * that import `t` from there are untouched.
 *
 * **Why three and not one.** The difference is where the key came from, which
 * is the only thing that decides what a miss means:
 *
 * | door | the key | a miss |
 * |---|---|---|
 * | `t` | one you typed | impossible: the compiler already refused it |
 * | `tData` | data the app trusts — a template's `labelKey` the validator proved, a stored `entryNounKey` | a defect, and it throws |
 * | `tOr` | data that may legitimately be unknown — a template id an older build applied, a source channel a future server invents | expected, and the fallback is drawn |
 *
 * A widened `t` that took any string would serve the 56 data sites by throwing
 * away the compiler's answer at the 1,597 typed ones, so the split is at the
 * door rather than in the signature.
 *
 * **It is synchronous, and that is a property and not an accident.** 185
 * render bodies call `t` in the middle of drawing. `i18next.ts` initialises at
 * import with English in hand, so the first call in the first render already
 * has a catalogue. What arrives over the air later arrives as an added bundle
 * and a language change, which is what `catalogueRevision` reports.
 */
import type { TOptions } from 'i18next';

import type { AppKey } from './en';
import { i18next } from './i18next';

/**
 * The one call into i18next.
 *
 * The cast is here and nowhere else. i18next's own key union is generated
 * from the same `en` object this package's `AppKey` is, so the two describe
 * the same set — but `t` is overloaded past the point where a wrapper can
 * name the overload it wants, and three doors casting separately would be
 * three places to get it wrong.
 */
const say = (key: string, options: TOptions | undefined): string =>
  i18next.t(key as never, options as never) as unknown as string;

/** A key you typed. The compiler checks it against the catalogue, stems included. */
export const t = (key: AppKey, options?: TOptions): string => say(key, options);

/**
 * The same lookup, for a key built from STORED DATA the app has already
 * proved: a template `labelKey` the validator resolved, a record's
 * `entryNounKey`. It throws on a miss exactly as `t` does, because a key that
 * passed validation and then resolved to nothing is a defect and not a gap.
 */
export const tData = (key: string, options?: TOptions): string => say(key, options);

/**
 * For a key from data that can be legitimately unknown.
 *
 * `t` throws on purpose: a key a screen wrote is either in the catalogue or it
 * is a defect. But a key assembled from a value on the device — a template id
 * an older build applied, a source channel a future server invents — can be
 * legitimately unknown, and crashing the strip over it would be worse than
 * showing the value itself.
 *
 * `exists` is asked first rather than catching the throw, because a plural
 * stem's existence depends on the count: `exists('noun.record')` is false and
 * `exists('noun.record', { count: 2 })` is true, and only the options decide
 * which question was asked.
 */
export const tOr = (key: string, fallback: string, options?: TOptions): string =>
  i18next.exists(key as never, options as never) ? say(key, options) : fallback;

/**
 * Bumped whenever the words change: a language put in force, or a downloaded
 * bundle added to the store.
 *
 * Both events matter and neither implies the other. `languageChanged` fires
 * when the tag is switched; `store.added` fires when a bundle lands, which on
 * the over-the-air path happens after the switch has already been requested.
 * A host that redraws on one of the two would miss the first paint of a
 * language it had just downloaded.
 */
let revision = 0;

const listeners = new Set<() => void>();

const changed = (): void => {
  revision += 1;
  // A copy, because a listener is allowed to unsubscribe itself while running.
  for (const listener of [...listeners]) {
    listener();
  }
};

i18next.on('languageChanged', changed);
i18next.store.on('added', changed);

/** What `useSyncExternalStore` reads. It changes only when the words change. */
export const catalogueRevision = (): number => revision;

export const onCatalogueChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * The same lookup, for the ~20 values in `packages/ui` that carry a name or a
 * figure inside the sentence.
 *
 * It is `t` with the options block spelled out, and it exists so the call
 * sites that arrived with the design system's own wording keep reading the way
 * they were written. The slot is i18next's `{{name}}`, filled from this
 * record; there is no second placeholder spelling and no `%s` anywhere in the
 * catalogue. A name the value does not carry is left where it stands by
 * i18next, and `interpolation.enforcement.test.ts` is what catches it.
 */
export const tWith = (key: AppKey, vars: Readonly<Record<string, string | number>>): string =>
  say(key, vars);
